/**
 * AppTopbar — redesign top bar (shared across Portfolio + property surfaces).
 *
 * Sticky, translucent canvas with backdrop blur and a 1px bottom border.
 * Left: optional breadcrumb + page title (Schibsted) + optional subtitle.
 * Right: Search (⌘K), notification bell (rose dot), and the "Ask Stayframe"
 * primary button (forest) which opens the concierge panel.
 *
 * Layout/markup mirrors the design handoff header exactly, expressed with
 * Tailwind utilities mapped to the --sf-* redesign tokens.
 */

import { Link } from "react-router"
import { Search, Bell, Sparkles, Menu } from "lucide-react"

export interface Crumb {
  label: string
  to?: string
}

interface AppTopbarProps {
  title?: string
  subtitle?: string
  breadcrumb?: Crumb[]
  hasNotification?: boolean
  onAsk: () => void
  onSearch?: () => void
  onBell?: () => void
  onMenuClick?: () => void
}

export function AppTopbar({
  title,
  subtitle,
  breadcrumb,
  hasNotification = true,
  onAsk,
  onSearch,
  onBell,
  onMenuClick,
}: AppTopbarProps) {
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3.5 px-4 py-3.5 md:px-7 border-b border-border bg-canvas/80 backdrop-blur-[12px]">
      {/* Mobile: open sidebar */}
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          className="md:hidden flex items-center justify-center h-9 w-9 -ml-1 rounded-[10px] border border-border bg-card text-ink-2 hover:bg-line-soft transition-colors"
          aria-label="Open navigation"
        >
          <Menu className="h-[18px] w-[18px]" />
        </button>
      )}

      <div className="flex-1 min-w-0">
        {breadcrumb && breadcrumb.length > 0 && (
          <nav className="flex items-center gap-1.5 mb-0.5 text-[12.5px]" aria-label="Breadcrumb">
            {breadcrumb.map((c, i) => (
              <span key={`${c.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-muted-foreground/40">/</span>}
                {c.to && i < breadcrumb.length - 1 ? (
                  <Link to={c.to} className="text-muted-foreground hover:text-brand-strong transition-colors">
                    {c.label}
                  </Link>
                ) : (
                  <span className={i === breadcrumb.length - 1 ? "text-ink-2 font-medium" : "text-muted-foreground"}>
                    {c.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
        )}
        {title && (
          <h1 className="font-display text-[19px] font-bold tracking-[-0.02em] text-foreground leading-tight truncate">
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="mt-px text-[12.5px] font-medium text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>

      {/* Search */}
      <button
        type="button"
        onClick={onSearch}
        className="hidden sm:flex items-center gap-2.5 px-3 py-2 rounded-[10px] border border-border bg-card text-muted-foreground text-[13px] font-medium hover:border-line-2 transition-colors"
      >
        <Search className="h-[15px] w-[15px]" strokeWidth={1.8} />
        Search
        <span className="font-mono text-[10.5px] px-1.5 border border-border rounded-[5px] text-muted-foreground">⌘K</span>
      </button>

      {/* Notifications — the dot signals a proactive concierge message; clicking opens it */}
      <button
        type="button"
        onClick={onBell}
        className="relative flex items-center justify-center h-[38px] w-[38px] rounded-[10px] border border-border bg-card text-ink-2 hover:bg-line-soft transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-[17px] w-[17px]" strokeWidth={1.7} />
        {hasNotification && (
          <span className="absolute top-2 right-2.5 h-[7px] w-[7px] rounded-full bg-rose border-[1.5px] border-card" />
        )}
      </button>

      {/* Ask Stayframe */}
      <button
        type="button"
        onClick={onAsk}
        className="flex items-center gap-2 px-[15px] py-[9px] rounded-[10px] bg-forest text-white text-[13px] font-semibold shadow-sm hover:opacity-90 transition-opacity"
      >
        <Sparkles className="h-[15px] w-[15px] fill-white" strokeWidth={0} />
        <span className="hidden sm:inline">Ask Stayframe</span>
        <span className="sm:hidden">Ask</span>
      </button>
    </header>
  )
}
